import { useState, useCallback } from 'react';
import {
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

/**
 * Parameters for the useColumnDnd hook
 * @template TItem - The type of items being dragged
 */
interface UseColumnDndParams<TItem> {
  /** Array of items that can be dragged */
  items: TItem[];
  /** Callback function called when items are reordered */
  onItemsReordered: (reorderedItems: TItem[]) => void | Promise<void>;
  /** Function to get the unique identifier for an item */
  getId: (item: TItem) => string | number;
  /** Optional callback function called when an error occurs */
  onError?: (error: unknown) => void;
}

/**
 * Return type for the useColumnDnd hook
 * @template TItem - The type of items being dragged
 */
interface UseColumnDndReturn<TItem> {
  /** The currently active (dragged) item */
  activeItem: TItem | null;
  /** Whether the items are currently being updated */
  isUpdating: boolean;
  /** Whether animations should be enabled */
  shouldAnimate: boolean;
  /** DnD sensors for handling drag events */
  sensors: ReturnType<typeof useSensors>;
  /** Handler for drag start events */
  handleDragStart: (event: DragStartEvent) => void;
  /** Handler for drag end events */
  handleDragEnd: (event: DragEndEvent) => void;
  /** Function to get the IDs of all items */
  getItemIds: () => string[];
}

/**
 * A hook for handling drag and drop functionality for columns
 * @template TItem - The type of items being dragged
 * @param params - The parameters for the hook
 * @returns An object containing the drag and drop state and handlers
 */
export function useColumnDnd<TItem>({
  items,
  onItemsReordered,
  getId,
  onError,
}: UseColumnDndParams<TItem>): UseColumnDndReturn<TItem> {
  const [activeItem, setActiveItem] = useState<TItem | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10,
    },
  });

  const sensors = useSensors(pointerSensor);

  /**
   * Resets the drag state
   * @param animate - Whether to animate the reset
   */
  function resetDragState(animate: boolean) {
    setShouldAnimate(animate);
    setActiveItem(null);
  }

  /**
   * Gets the IDs of all items with the 'item-' prefix
   * @returns Array of item IDs
   */
  const getItemIds = useCallback(() => {
    return items.map((item) => `item-${getId(item)}`);
  }, [items, getId]);

  /**
   * Handles the start of a drag operation
   * @param event - The drag start event
   */
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const itemId = event.active.id.toString();
      const itemPrefix = 'item-';

      if (!itemId.startsWith(itemPrefix)) return;

      const id = itemId.slice(itemPrefix.length);
      const item = items.find((item) => getId(item).toString() === id);

      if (item) {
        resetDragState(true);
        setActiveItem(item);
      }
    },
    [items, getId]
  );

  /**
   * Handles the end of a drag operation
   * @param event - The drag end event
   */
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        resetDragState(true);
        return;
      }

      try {
        const activeId = active.id.toString();
        const overId = over.id.toString();
        const itemPrefix = 'item-';

        if (
          !activeId.startsWith(itemPrefix) ||
          !overId.startsWith(itemPrefix)
        ) {
          resetDragState(true);
          return;
        }

        const activeItemId = activeId.slice(itemPrefix.length);
        const overItemId = overId.slice(itemPrefix.length);

        const oldIndex = items.findIndex(
          (item) => getId(item).toString() === activeItemId
        );
        const newIndex = items.findIndex(
          (item) => getId(item).toString() === overItemId
        );

        if (oldIndex === -1 || newIndex === -1) {
          resetDragState(true);
          return;
        }

        if (oldIndex !== newIndex) {
          setShouldAnimate(false);
          setIsUpdating(true);

          const reorderedItems = arrayMove([...items], oldIndex, newIndex).map(
            (item, index) => ({
              ...item,
              position: index,
            })
          );

          setActiveItem(null);
          await onItemsReordered(reorderedItems);
        } else {
          resetDragState(true);
        }
      } catch (error) {
        onError?.(error);
        resetDragState(true);
      } finally {
        setIsUpdating(false);
      }
    },
    [items, getId, onItemsReordered, onError]
  );

  return {
    activeItem,
    isUpdating,
    shouldAnimate,
    sensors,
    handleDragStart,
    handleDragEnd,
    getItemIds,
  };
}
